import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Phone } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { COMPANY } from "@/lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CANONICAL = "https://alldaypowerwashing.com/quote";

const TIMEFRAME_OPTIONS = [
  "ASAP / this week",
  "Within 2 weeks",
  "This month",
  "Next 1–3 months",
  "Just getting a price for now",
];

const REFERRAL_OPTIONS = [
  "Google search",
  "Google Maps",
  "Facebook",
  "Instagram",
  "YouTube",
  "Friend / word of mouth",
  "Saw our truck or yard sign",
  "Yelp / BBB",
  "Repeat customer",
  "Other",
];

const initialForm = {
  name: "",
  phone: "",
  email: "",
  address: "",
  property_type: "",
  service: "",
  property_size: "",
  timeframe: "",
  message: "",
  referral_source: "",
  sms_consent: false,
};

export default function QuotePage() {
  const [form, setForm] = useState(initialForm);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    // Optional URL pre-fill from the popup: ?type=residential|commercial
    try {
      const t = new URLSearchParams(window.location.search).get("type");
      if (t === "residential" || t === "commercial") {
        setForm((f) => ({ ...f, property_type: t }));
      }
    } catch { /* ignore */ }
  }, []);

  const canSubmit = useMemo(
    () =>
      form.name.trim() &&
      form.phone.trim() &&
      form.email.trim() &&
      form.property_type &&
      form.service &&
      form.sms_consent,
    [form]
  );

  const submit = async (e) => {
    e?.preventDefault();
    if (!canSubmit || loading) return;
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        // Compliance audit trail — record when user gave consent
        sms_consent_at: form.sms_consent ? new Date().toISOString() : null,
        // Required by backend model — filled with sensible defaults
        size_tier: "medium",
        source: "quote_form",
      });
      setSubmitted(true);
      toast.success("Got it — we'll text you back within 24 hours.");
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.detail?.[0]?.msg ||
        "Something went wrong. Call or text 719-644-6105."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B1726] text-white flex flex-col" data-testid="quote-page">
      <Helmet>
        <title>Get a Free Quote — All Day Power Washing</title>
        <meta
          name="description"
          content="Fill out our simple quote request form. Residential & commercial pressure washing in Colorado Springs and Denver. Same-week scheduling. Veteran owned."
        />
        <link rel="canonical" href={CANONICAL} />
        <meta property="og:title" content="Get a Free Quote — All Day Power Washing" />
        <meta property="og:url" content={CANONICAL} />
      </Helmet>

      <Header />

      {/* Compact form area — no big hero pushing content down */}
      <main className="flex-1 pt-24 sm:pt-28 pb-16 px-4 sm:px-6">
        <div className="max-w-2xl mx-auto">
          <Link
            to="/"
            data-testid="quote-back-home"
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-xs font-heading uppercase tracking-wider mb-4 transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to home
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="bg-white rounded-lg shadow-[0_20px_60px_rgba(0,0,0,0.35)] overflow-hidden"
          >
            {submitted ? (
              <div className="p-8 sm:p-10 text-center" data-testid="quote-success">
                <div className="w-14 h-14 rounded-full bg-[#1E88E5]/10 border-2 border-[#1E88E5] flex items-center justify-center mx-auto mb-5">
                  <Check className="w-7 h-7 text-[#1E88E5]" strokeWidth={2.5} />
                </div>
                <h1 className="font-heading text-2xl sm:text-3xl font-black uppercase tracking-tight text-slate-900 mb-3">
                  Got it, {form.name.split(" ")[0]}.
                </h1>
                <p className="text-slate-600 max-w-md mx-auto">
                  We received your quote request and will text or call you within 24 hours — usually a lot sooner.
                </p>
                <a
                  href={COMPANY.phoneHref}
                  className="inline-flex items-center gap-2 mt-6 text-[#1E88E5] hover:text-[#0B1726] font-heading uppercase tracking-wider text-sm transition-colors"
                >
                  <Phone className="w-4 h-4" /> Or call/text {COMPANY.phone}
                </a>
              </div>
            ) : (
              <form
                onSubmit={submit}
                data-testid="quote-form"
                className="p-6 sm:p-8"
              >
                {/* Compact title inside the card */}
                <div className="mb-6">
                  <h1 className="font-heading text-2xl sm:text-3xl font-black uppercase tracking-tight text-slate-900">
                    Request a Quote
                  </h1>
                  <p className="text-slate-500 text-sm mt-1">
                    Fill this out. We&apos;ll text you back within 24 hours.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <TextField
                    label="Full name"
                    required
                    value={form.name}
                    onChange={(v) => setForm({ ...form, name: v })}
                    testid="quote-name"
                    autoComplete="name"
                  />
                  <TextField
                    label="Phone number"
                    type="tel"
                    required
                    value={form.phone}
                    onChange={(v) => setForm({ ...form, phone: v })}
                    testid="quote-phone"
                    autoComplete="tel"
                  />
                  <TextField
                    label="Email"
                    type="email"
                    required
                    value={form.email}
                    onChange={(v) => setForm({ ...form, email: v })}
                    testid="quote-email"
                    autoComplete="email"
                  />
                  <TextField
                    label="Property address"
                    value={form.address}
                    onChange={(v) => setForm({ ...form, address: v })}
                    testid="quote-address"
                    autoComplete="street-address"
                  />
                </div>

                {/* Property type */}
                <div className="mt-5">
                  <FieldLabel required>Residential or commercial?</FieldLabel>
                  <div className="grid grid-cols-2 gap-3 mt-2">
                    {[
                      { id: "residential", label: "Residential" },
                      { id: "commercial", label: "Commercial" },
                    ].map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setForm({ ...form, property_type: p.id })}
                        data-testid={`quote-property-${p.id}`}
                        className={`px-4 py-3 rounded-md border-2 font-heading uppercase tracking-wider text-sm transition-all ${
                          form.property_type === p.id
                            ? "bg-[#1E88E5] border-[#1E88E5] text-white"
                            : "bg-white border-slate-200 text-slate-600 hover:border-slate-400"
                        }`}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Service — free text so people can describe exactly what they need */}
                <div className="mt-5">
                  <FieldLabel required>What do you need cleaned?</FieldLabel>
                  <textarea
                    rows={3}
                    value={form.service}
                    onChange={(e) => setForm({ ...form, service: e.target.value })}
                    data-testid="quote-service"
                    placeholder="Example: House siding, driveway, and back patio. About 2,000 sqft. Lots of algae on the north wall."
                    className="mt-2 w-full bg-white border border-slate-300 rounded-md px-3 py-3 text-slate-900 text-sm placeholder:text-slate-400 focus:border-[#1E88E5] focus:ring-2 focus:ring-[#1E88E5]/20 outline-none transition-all resize-none"
                  />
                </div>

                {/* Property size + preferred timeframe */}
                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <TextField
                    label="Approximate property size"
                    value={form.property_size}
                    onChange={(v) => setForm({ ...form, property_size: v })}
                    testid="quote-property-size"
                    placeholder="e.g. 2,400 sqft or 3-bed home"
                  />
                  <div>
                    <FieldLabel>Preferred timeframe</FieldLabel>
                    <select
                      value={form.timeframe}
                      onChange={(e) => setForm({ ...form, timeframe: e.target.value })}
                      data-testid="quote-timeframe"
                      className="mt-2 w-full bg-white border border-slate-300 rounded-md px-3 py-3 text-slate-900 text-sm focus:border-[#1E88E5] focus:ring-2 focus:ring-[#1E88E5]/20 outline-none transition-all appearance-none cursor-pointer"
                    >
                      <option value="">Select…</option>
                      {TIMEFRAME_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Anything else */}
                <div className="mt-5">
                  <FieldLabel>Anything else? (optional)</FieldLabel>
                  <textarea
                    rows={3}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    data-testid="quote-message"
                    placeholder="Access notes, gate codes, pets, stains to focus on, etc."
                    className="mt-2 w-full bg-white border border-slate-300 rounded-md px-3 py-3 text-slate-900 text-sm placeholder:text-slate-400 focus:border-[#1E88E5] focus:ring-2 focus:ring-[#1E88E5]/20 outline-none transition-all resize-none"
                  />
                </div>

                {/* Referral */}
                <div className="mt-5">
                  <FieldLabel>How did you hear about us? (optional)</FieldLabel>
                  <select
                    value={form.referral_source}
                    onChange={(e) => setForm({ ...form, referral_source: e.target.value })}
                    data-testid="quote-referral"
                    className="mt-2 w-full bg-white border border-slate-300 rounded-md px-3 py-3 text-slate-900 text-sm focus:border-[#1E88E5] focus:ring-2 focus:ring-[#1E88E5]/20 outline-none transition-all appearance-none cursor-pointer"
                  >
                    <option value="">Select an option…</option>
                    {REFERRAL_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>

                <label
                  className="mt-6 flex items-start gap-3 cursor-pointer"
                  data-testid="quote-sms-consent-label"
                >
                  <input
                    type="checkbox"
                    checked={form.sms_consent}
                    onChange={(e) => setForm({ ...form, sms_consent: e.target.checked })}
                    data-testid="quote-sms-consent"
                    required
                    className="mt-1 h-4 w-4 flex-shrink-0 rounded border-slate-400 text-[#1E88E5] focus:ring-2 focus:ring-[#1E88E5]/30 cursor-pointer"
                  />
                  <span className="text-[11px] sm:text-xs text-slate-600 leading-relaxed">
                    I agree to receive text messages from All Day Power Washing about service updates, appointment reminders, quotes, and occasional offers. Message frequency varies. Msg &amp; data rates may apply. Reply <strong>STOP</strong> to opt out, <strong>HELP</strong> for help. Consent is not required to use our services.{" "}
                    <a
                      href="/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline text-[#1E88E5] hover:text-[#0B1726]"
                    >
                      Privacy Policy
                    </a>
                    {" | "}
                    <a
                      href="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline text-[#1E88E5] hover:text-[#0B1726]"
                    >
                      Terms
                    </a>
                  </span>
                </label>

                <button
                  type="submit"
                  disabled={!canSubmit || loading}
                  data-testid="quote-submit-btn"
                  className="mt-5 w-full bg-[#1E88E5] hover:bg-[#42A5F5] disabled:bg-slate-300 disabled:text-slate-500 text-white font-heading font-black tracking-[0.15em] uppercase text-sm sm:text-base px-6 py-4 rounded-md flex items-center justify-center gap-2 transition-all"
                >
                  {loading ? "Sending…" : (
                    <>Submit Request <ArrowRight className="w-4 h-4" /></>
                  )}
                </button>

                <div className="mt-4 flex items-center justify-between text-[11px] text-slate-500">
                  <span>No spam. No sales calls.</span>
                  <a
                    href={COMPANY.phoneHref}
                    className="inline-flex items-center gap-1 hover:text-[#1E88E5] transition-colors"
                  >
                    <Phone className="w-3 h-3" /> {COMPANY.phone}
                  </a>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

/* ---------- form atoms ---------- */
const FieldLabel = ({ children, required }) => (
  <label className="font-heading text-[11px] font-bold tracking-[0.15em] uppercase text-slate-500">
    {children} {required && <span className="text-[#E6B445]">*</span>}
  </label>
);

const TextField = ({ label, value, onChange, type = "text", required = false, testid, autoComplete, placeholder }) => (
  <div>
    <FieldLabel required={required}>{label}</FieldLabel>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      autoComplete={autoComplete}
      placeholder={placeholder}
      className="mt-2 w-full bg-white border border-slate-300 rounded-md px-3 py-3 text-slate-900 text-sm placeholder:text-slate-400 focus:border-[#1E88E5] focus:ring-2 focus:ring-[#1E88E5]/20 outline-none transition-all"
    />
  </div>
);
